import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Divider,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  ListItemIcon,
  CircularProgress,
  Tooltip,
  Fab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Receipt as ReceiptIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { Expense, getExpenses, deleteExpense, subscribeToExpenses } from '../firebase/expenseServices';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ExpenseForm from './ExpenseForm';
import GLTransactionView from '../components/GLTransactionView';

// Category options
const categories = [
  'All Categories',
  'Rent',
  'Supplies',
  'Payroll',
  'Software',
  'Utilities',
  'Marketing',
  'Insurance',
  'Maintenance',
  'Training',
  'Equipment',
  'Office Supplies',
  'Travel',
  'Meals',
  'Taxes',
  'Other'
];

// Payment method options
const paymentMethods = [
  'All Methods',
  'Bank Transfer',
  'Credit Card',
  'Cash',
  'Direct Debit',
  'Other'
];

// Status options
const statuses = [
  'All Statuses',
  'paid',
  'pending',
  'cancelled'
];

const Expenses: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  // State for expenses data
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for filtering and pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('All Categories');
  const [methodFilter, setMethodFilter] = useState('All Methods');
  const [statusFilter, setStatusFilter] = useState('All Statuses');

  // State for menu and dialogs
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedExpenseId, setSelectedExpenseId] = useState<string | null>(null);
  const [expenseFormOpen, setExpenseFormOpen] = useState(false);
  const [editExpenseId, setEditExpenseId] = useState<string | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [glTransactionViewOpen, setGLTransactionViewOpen] = useState(false);
  const [selectedExpenseTitle, setSelectedExpenseTitle] = useState<string>('');

  // Fetch expenses data
  useEffect(() => {
    setLoading(true);
    setError(null);

    // Subscribe to real-time expense updates
    const unsubscribe = subscribeToExpenses(
      (expensesData) => {
        setExpenses(expensesData);
        setLoading(false);
      },
      startDate,
      endDate
    );

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [startDate, endDate]);

  // Handle errors
  useEffect(() => {
    if (error) {
      enqueueSnackbar(`Error: ${error}`, { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  // Filtered expenses based on search and filters
  const filteredExpenses = expenses.filter((expense) => {
    const matchesSearch =
      expense.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      expense.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (expense.vendorName && expense.vendorName.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCategory = categoryFilter === 'All Categories' || expense.category === categoryFilter;
    const matchesMethod = methodFilter === 'All Methods' || expense.method === methodFilter;
    const matchesStatus = statusFilter === 'All Statuses' || expense.status === statusFilter;

    return matchesSearch && matchesCategory && matchesMethod && matchesStatus;
  });

  // Handle pagination changes
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, id: string, title: string = '') => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedExpenseId(id);
    
    // Find the expense title if not provided
    if (!title && id) {
      const expense = expenses.find(exp => exp.id === id);
      if (expense) {
        setSelectedExpenseTitle(expense.title);
      }
    } else {
      setSelectedExpenseTitle(title);
    }
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    // Don't clear selectedExpenseId here, as it's needed for delete operation
  };

  // Handle edit expense
  const handleEditExpense = () => {
    if (selectedExpenseId) {
      setEditExpenseId(selectedExpenseId);
      setExpenseFormOpen(true);
    }
    handleMenuClose();
  };

  // Handle add new expense
  const handleAddExpense = () => {
    setEditExpenseId(undefined);
    setExpenseFormOpen(true);
  };

  // Handle expense form close
  const handleExpenseFormClose = () => {
    setExpenseFormOpen(false);
    setEditExpenseId(undefined);
  };

  // Handle expense form save
  const handleExpenseFormSave = () => {
    enqueueSnackbar(editExpenseId ? 'Expense updated successfully' : 'Expense added successfully', { variant: 'success' });
  };
  
  // Handle view GL entries
  const handleViewGLEntries = () => {
    if (selectedExpenseId) {
      setGLTransactionViewOpen(true);
    }
    handleMenuClose();
  };
  
  // Handle GL transaction view close
  const handleGLTransactionViewClose = () => {
    setGLTransactionViewOpen(false);
  };

  // Handle delete confirmation dialog
  const handleDeleteConfirm = () => {
    // Make sure selectedExpenseId is set before opening the dialog
    if (selectedExpenseId) {
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };
  
  // Direct delete button handler (for the delete button in the dialog)
  const handleDeleteButtonClick = (expenseId: string) => {
    setSelectedExpenseId(expenseId);
    setDeleteDialogOpen(true);
  };

  // Handle delete expense
  const handleDeleteExpense = async () => {
    console.log('Delete button clicked, selectedExpenseId:', selectedExpenseId);
    if (selectedExpenseId) {
      setDeleteInProgress(true);
      try {
        console.log('Attempting to delete expense with ID:', selectedExpenseId);
        await deleteExpense(selectedExpenseId);
        console.log('Expense deleted successfully');
        enqueueSnackbar('Expense deleted successfully', { variant: 'success' });
      } catch (error) {
        console.error('Error deleting expense:', error);
        enqueueSnackbar('Failed to delete expense', { variant: 'error' });
      } finally {
        setDeleteInProgress(false);
        setDeleteDialogOpen(false);
        setSelectedExpenseId(null);
      }
    } else {
      console.error('No expense ID selected for deletion');
      enqueueSnackbar('No expense selected for deletion', { variant: 'error' });
      setDeleteDialogOpen(false);
    }
  };

  // Calculate total expenses for the current filtered view
  const totalFilteredAmount = filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h1" fontWeight={600}>
          Expenses
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddExpense}
        >
          Add Expense
        </Button>
      </Box>

      <Paper sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          {/* Search field */}
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search expenses..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ flexGrow: 1, minWidth: '200px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* Filter button */}
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            size="small"
            onClick={(event) => setFilterAnchorEl(event.currentTarget)}
          >
            Filter
          </Button>

          {/* Filter menu */}
          <Menu
            anchorEl={filterAnchorEl}
            open={Boolean(filterAnchorEl)}
            onClose={() => setFilterAnchorEl(null)}
            PaperProps={{
              sx: { width: '250px', p: 1 }
            }}
          >
            <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>Filter Expenses</Typography>
            <Divider sx={{ mb: 1 }} />

            {/* Category filter */}
            <FormControl size="small" sx={{ m: 1, width: 'calc(100% - 16px)' }}>
              <InputLabel id="category-filter-label">Category</InputLabel>
              <Select
                labelId="category-filter-label"
                value={categoryFilter}
                label="Category"
                onChange={(event: SelectChangeEvent) => setCategoryFilter(event.target.value)}
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Payment method filter */}
            <FormControl size="small" sx={{ m: 1, width: 'calc(100% - 16px)' }}>
              <InputLabel id="method-filter-label">Payment Method</InputLabel>
              <Select
                labelId="method-filter-label"
                value={methodFilter}
                label="Payment Method"
                onChange={(event: SelectChangeEvent) => setMethodFilter(event.target.value)}
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method} value={method}>{method}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Status filter */}
            <FormControl size="small" sx={{ m: 1, width: 'calc(100% - 16px)' }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={(event: SelectChangeEvent) => setStatusFilter(event.target.value)}
              >
                {statuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status === 'All Statuses' ? status : (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                          size="small"
                          label={status}
                          color={status === 'paid' ? 'success' : 'warning'}
                          sx={{ mr: 1, textTransform: 'capitalize' }}
                        />
                      </Box>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, px: 1 }}>
              <Button
                size="small"
                onClick={() => {
                  setCategoryFilter('All Categories');
                  setMethodFilter('All Methods');
                  setStatusFilter('All Statuses');
                  setSearchQuery('');
                  setFilterAnchorEl(null);
                }}
              >
                Reset Filters
              </Button>
            </Box>
          </Menu>
        </Box>

        {/* Current filter tags display */}
        {(categoryFilter !== 'All Categories' || methodFilter !== 'All Methods' || statusFilter !== 'All Statuses' || searchQuery) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {categoryFilter !== 'All Categories' && (
              <Chip
                label={`Category: ${categoryFilter}`}
                size="small"
                onDelete={() => setCategoryFilter('All Categories')}
              />
            )}
            {methodFilter !== 'All Methods' && (
              <Chip
                label={`Method: ${methodFilter}`}
                size="small"
                onDelete={() => setMethodFilter('All Methods')}
              />
            )}
            {statusFilter !== 'All Statuses' && (
              <Chip
                label={`Status: ${statusFilter}`}
                size="small"
                color={statusFilter === 'paid' ? 'success' : 'warning'}
                onDelete={() => setStatusFilter('All Statuses')}
                sx={{ textTransform: 'capitalize' }}
              />
            )}
            {searchQuery && (
              <Chip
                label={`Search: ${searchQuery}`}
                size="small"
                onDelete={() => setSearchQuery('')}
              />
            )}
          </Box>
        )}

        {/* Expenses summary */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {loading ? (
              'Loading expenses...'
            ) : (
              `Showing ${filteredExpenses.length} ${filteredExpenses.length === 1 ? 'expense' : 'expenses'}`
            )}
          </Typography>
          <Typography variant="subtitle2" fontWeight={600}>
            Total: RM {totalFilteredAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        </Box>

        {/* Expenses table */}
        <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    <Typography variant="body2" color="text.secondary" component="span">
                      Loading expenses...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredExpenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No expenses found matching your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredExpenses
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((expense) => (
                    <TableRow key={expense.id} hover>
                      <TableCell>
                        {typeof expense.date === 'object' && 'seconds' in expense.date
                          ? format(new Date(expense.date.seconds * 1000), 'dd/MM/yyyy')
                          : expense.date instanceof Date
                            ? format(expense.date, 'dd/MM/yyyy')
                            : format(new Date(expense.date), 'dd/MM/yyyy')}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={expense.description || ''} arrow placement="top-start">
                          <Typography variant="body2">{expense.title}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{expense.category}</TableCell>
                      <TableCell>{expense.method}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={expense.status}
                          color={expense.status === 'paid' ? 'success' : expense.status === 'pending' ? 'warning' : 'error'}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500 }}>
                        RM {expense.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, expense.id as string)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredExpenses.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Floating Action Button for mobile */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, position: 'fixed', bottom: 20, right: 20 }}>
        <Fab color="primary" aria-label="add" onClick={handleAddExpense}>
          <AddIcon />
        </Fab>
      </Box>

      {/* Expense Form Dialog */}
      <ExpenseForm
        open={expenseFormOpen}
        onClose={handleExpenseFormClose}
        expenseId={editExpenseId}
        onSave={handleExpenseFormSave}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        TransitionProps={{
          onEnter: () => {
            // Ensure we have the expense ID when the dialog opens
            console.log('Dialog opened with expense ID:', selectedExpenseId);
          }
        }}
      >
        <DialogTitle id="alert-dialog-title">
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">Delete Expense</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this expense? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteInProgress}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              console.log('Delete button clicked in dialog, ID:', selectedExpenseId);
              handleDeleteExpense();
            }}
            color="error"
            variant="contained"
            disabled={deleteInProgress}
            startIcon={deleteInProgress ? <CircularProgress size={20} /> : <DeleteIcon />}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditExpense}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem onClick={handleViewGLEntries}>
          <ListItemIcon>
            <AccountBalanceIcon fontSize="small" color="primary" />
          </ListItemIcon>
          View Entries
        </MenuItem>
        <MenuItem onClick={handleDeleteConfirm}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography color="error">Delete</Typography>
        </MenuItem>
      </Menu>
      
      {/* GL Transaction View Dialog */}
      {selectedExpenseId && (
        <GLTransactionView
          open={glTransactionViewOpen}
          onClose={handleGLTransactionViewClose}
          expenseId={selectedExpenseId}
          expenseTitle={selectedExpenseTitle}
        />
      )}
    </Box>
  );
};

export default Expenses;